/**!
 * Apricot DX Profile
 * @version 2
 * @author Mitra Assadi
 * @license
 * Copyright (c) 2026 The College Board
 */

/* ========================================================================
 * Apricot's D8
 * ======================================================================== */

// SCSS
import '../scss/dx/apricot-dx-profile.scss';

// javaScript
import Utils from './CBUtils';
import CBAccordion from './CBAccordion';

/**
 * Menu Page Layout
 *
 * @export
 * @param {Object} data
 * @param {Element} data.elem
 * @returns {{destroy: Function}}
 */
const menuPageLayout = (data = {}) => {
  const defaultData = {
    elem: null,
  };

  data = {
    ...defaultData,
    ...data,
  };

  let elem = data.elem;

  if (!Utils.elemExists(elem)) return null;

  const chunkArray = (arr, chunk) => {
    let results = [];

    while (arr.length) {
      results.push(arr.splice(0, chunk));
    }

    return results;
  };

  const checkLayout = viewport => {
    let columns = 3;
    if (viewport === 'xs') {
      columns = 1;
    } else if (viewport === 'sm') {
      columns = 2;
    }

    const menus = [];

    elem
      .querySelectorAll('.cb-menu-list-d9 .cb-menu-list-heading-link, .cb-menu-list-d9 .cb-menu-list-heading')
      .forEach(node => {
        node.style.height = 'auto';

        node.style.minHeight = 'auto';

        if (columns > 1) {
          const obj = {};

          obj.height = Utils.outerHeight(node);

          obj.elem = node;

          menus.push(obj);
        }
      });

    if (columns === 1) return;

    let items = chunkArray(menus, columns);

    for (var item in items) {
      const row = items[item];

      row.sort((a, b) => {
        return a.height - b.height;
      });

      const max = row[row.length - 1].height;

      Array.prototype.forEach.call(row, obj => {
        obj.elem.style.minHeight = `${max}px`;

        obj.elem.style.height = `${max}px`;
      });
    }
  };

  const breakpointChange = e => {
    const breakpointData = e.data;

    checkLayout(breakpointData.prefix);
  };

  const destroy = () => {
    if (elem.menuPageLayout === 'cb') {
      elem.menuPageLayout = null;
    }

    document.removeEventListener('apricot_breakpointChange', breakpointChange);
  };

  const init = () => {
    elem.menuPageLayout = 'cb';

    checkLayout(Utils.viewport().prefix);

    Utils.breakpoints();

    document.addEventListener('apricot_breakpointChange', breakpointChange);
  };

  if (elem.menuPageLayout !== 'cb') {
    init();
  }

  return {
    destroy: destroy,
  };
};

// --------------------------------

/**
 * Drupal Left Navigation
 *
 * @export
 * @param {Element} data.leftNav
 * @param {Element} data.leftNavTrigger
 * @param {String} data.mainContentId
 * @param {Array} data.mobileLayoutBreakpoints
 * @param {String} data.localNavMobileLayoutBreakpoint
 * @returns {{destroy: Function}}
 */
const dxLeftNavigation = (data = {}) => {
  const defaultData = {
    leftNav: null,
    leftNavTrigger: null,
    mainContentId: null,
    mobileLayoutBreakpoints: ['xs', 'sm'],
    localNavMobileLayoutBreakpoint: 'xs',
  };

  data = { ...defaultData, ...data };

  let leftNav = data.leftNav;
  let leftNavTrigger = data.leftNavTrigger;

  let firstLink = null;
  let leftNavClose = null;
  let cover = null;
  let skip = null;

  if (!Utils.elemExists(leftNav) || !Utils.elemExists(leftNavTrigger)) {
    return false;
  }

  // a11y
  const escKeyboard = e => {
    if (e.key === 'Escape' || e.keyCode === 27) {
      if (Utils.hasClass(leftNav, 'dx-nav-slider-show')) {
        cover.click();
      }
    }
  };

  const getScrollTop = () => {
    return window.scrollY || document.documentElement.scrollTop;
  };

  // hide: true -> hide all links
  // hide: false -> show all links
  const a11yAcc = hide => {
    const containers = leftNav.querySelectorAll('.cb-accordion-container');
    let anchors;

    containers.forEach(container => {
      if (hide) {
        anchors = container.querySelectorAll('a');

        anchors.forEach(a => {
          Utils.attr(a, 'tabIndex', '-1');

          Utils.attr(a, 'aria-hidden', 'true');
        });
      } else {
        // we only want to change the ones in open panel
        const heading = container.querySelector('.cb-accordion-heading');

        Utils.attr(heading, 'tabIndex', '0');

        Utils.attr(heading, 'aria-hidden', 'false');

        if (Utils.hasClass(heading, 'cb-active')) {
          anchors = container.querySelectorAll('.cb-accordion-panel a');

          anchors.forEach(a => {
            Utils.attr(a, 'tabIndex', '0');

            Utils.attr(a, 'aria-hidden', 'false');
          });
        }
      }
    });

    // firstLink = leftNav.querySelectorAll('a[aria-hidden="false"]')[0];
    // Utils.addClass(firstLink, "dx-first-link");
  };

  const keydownEvent = e => {
    const linkSelectors = 'a[aria-hidden="false"], .cb-accordion-heading';

    const a = e.target;
    // Clean up
    const predLastLink = leftNav.querySelector('.dx-last-link');

    Utils.removeClass(predLastLink, 'dx-last-link');

    const lastLinkList = leftNav.querySelectorAll(linkSelectors);
    const lastLink = lastLinkList[lastLinkList.length - 1];

    Utils.addClass(lastLink, 'dx-last-link');

    if (Utils.hasClass(a, 'dx-last-link')) {
      if (Utils.whichKey(e) === 'TAB' && !e.shiftKey) {
        e.preventDefault();

        firstLink.focus();
      }
    }

    if (Utils.hasClass(a, 'dx-first-link')) {
      if (Utils.whichKey(e) === 'TAB' && e.shiftKey) {
        e.preventDefault();

        lastLink.focus();
      }
    }
  };

  const tabOrder = () => {
    const linkSelectors = 'a[aria-hidden="false"], .cb-accordion-heading';
    const list = leftNav.querySelectorAll(linkSelectors);

    if (list.length <= 0) return;

    leftNav.querySelectorAll('a').forEach(a => {
      a.removeEventListener('keydown', keydownEvent);

      a.addEventListener('keydown', keydownEvent);
    });
  };

  // open/close Mobile panel
  const manageLeftNav = e => {
    e && e.preventDefault();

    // If open, close
    if (Utils.hasClass(leftNav, 'dx-nav-slider-show')) {
      Utils.removeClass(leftNav, 'dx-nav-slider-show');

      Utils.addClass(leftNav, 'dx-nav-slider-hide');

      Utils.attr(leftNavTrigger, 'aria-expanded', 'false');

      Utils.removeClass(leftNav, 'dx-nav-slider-hide');

      Utils.attr(leftNav, 'aria-hidden', 'true');

      Utils.attr(leftNav, 'tabIndex', '-1');

      Utils.removeClass(cover, 'dx-open');

      // Set focus back to trigger
      leftNavTrigger.focus();

      // add links to tab order
      a11yAcc(true);

      // Set focus to close
      Utils.attr(firstLink, 'tabIndex', '-1');

      Utils.attr(firstLink, 'aria-hidden', 'true');

      const body = document.getElementsByTagName('body')[0];

      Utils.removeClass(body, 'dx-left-nav-open');
    } else {
      tabOrder();

      Utils.attr(leftNavTrigger, 'aria-expanded', 'true');
      const pagePosition = getScrollTop();
      let topPosition = 40;
      if (pagePosition > 40) {
        const topNav = document.querySelector('.cb-local-navigation');
        if (Utils.elemExists(topNav) && Utils.hasClass(topNav, 'cb-sticky-local-navigation')) {
          const prefix = Utils.viewport().prefix;
          if (prefix !== data.localNavMobileLayoutBreakpoint) {
            topPosition = 72;
          } else {
            topPosition = 48;
          }
        } else {
          topPosition = 0;
        }
      } else if (pagePosition < 40 && pagePosition > 0) {
        topPosition = 40 - pagePosition;
      }
      leftNav.style.top = `${topPosition}px`;

      setTimeout(() => {
        Utils.addClass(leftNav, 'dx-nav-slider-show');
      }, 100);

      const body = document.getElementsByTagName('body')[0];

      Utils.addClass(body, 'dx-left-nav-open');

      Utils.attr(leftNav, 'aria-hidden', 'false');

      Utils.attr(leftNav, 'tabIndex', '0');

      Utils.addClass(cover, 'dx-open');

      // Set focus to close
      Utils.attr(firstLink, 'tabIndex', '0');

      Utils.attr(firstLink, 'aria-hidden', 'false');

      a11yAcc(false);

      leftNav.focus();
    }
  };

  const closeShowEvents = () => {
    cover.addEventListener('click', manageLeftNav);

    leftNavTrigger.addEventListener('click', manageLeftNav);

    leftNavClose.addEventListener('click', manageLeftNav);
  };

  const useMobileLayout = prefix => {
    return data.mobileLayoutBreakpoints.includes(prefix);
  };

  // switch between mobile/desktop
  const desktopMobileSwitch = mode => {
    // mode: true - > Mobile
    // mode: false -> Desktop
    if (mode) {
      Utils.attr(leftNavTrigger, 'tabIndex', '0');

      Utils.attr(leftNavTrigger, 'aria-hidden', 'false');

      Utils.attr(leftNav, 'aria-hidden', 'true');

      Utils.attr(leftNav, 'tabIndex', '-1');

      skip && Utils.attr(skip, 'tabIndex', '-1');

      skip && Utils.attr(skip, 'aria-hidden', 'true');
    } else {
      // make sure mobile layout is closed
      if (cover && Utils.hasClass(cover, 'dx-open')) {
        manageLeftNav();
      }
      Utils.attr(leftNavTrigger, 'tabIndex', '-1');

      Utils.attr(leftNavTrigger, 'aria-hidden', 'true');

      Utils.attr(leftNav, 'aria-hidden', 'false');

      Utils.attr(leftNav, 'tabIndex', '0');

      skip && Utils.removeAttr(skip, 'tabIndex', '0');

      skip && Utils.attr(skip, 'aria-hidden', 'false');
    }
  };
  const adjustLeftNavLayout = prefix => {
    // Accessibility for left nav trigger
    if (useMobileLayout(prefix)) {
      // Show trigger
      desktopMobileSwitch(true);

      a11yAcc(true);
    } else {
      // Hide trigger
      desktopMobileSwitch(false);

      a11yAcc(false);
    }
  };
  const trackBreakpointChanges = () => {
    document.addEventListener('apricot_breakpointChange', e => {
      if (!Utils.isEmptyObject(e.data)) {
        adjustLeftNavLayout(e.data.prefix);
      }
    });
  };

  const leftNavInteraction = () => {
    const elem = Utils.getByClass('cb-selected', leftNav)[0];

    elem && Utils.attr(elem, 'aria-current', 'page');
  };

  const destroy = () => {
    if (leftNav.dxLeftNavigation === 'cb') {
      leftNav.dxLeftNavigation = null;

      window.removeEventListener('keydown', escKeyboard, true);

      cover && cover.removeEventListener('click', manageLeftNav);

      leftNavTrigger && leftNavTrigger.removeEventListener('click', manageLeftNav);

      leftNavClose && leftNavClose.removeEventListener('click', manageLeftNav);

      leftNav.querySelectorAll('a').forEach(a => {
        a.removeEventListener('keydown', keydownEvent);
      });
    }
  };

  const init = () => {
    leftNav.dxLeftNavigation = 'cb';

    // Add cover
    cover = document.createElement('div');

    Utils.addClass(cover, 'dx-left-nav-cover');

    document.querySelector('body').appendChild(cover);

    // due Drupal structure
    const parent = leftNav.parentNode;
    if (Utils.elemExists(parent)) {
      Utils.addClass(parent, 'dx-left-nav-parent');
    }

    const triggerID = Utils.attr(leftNavTrigger, 'id')
      ? Utils.attr(leftNavTrigger, 'id')
      : Utils.uniqueID(5, 'apricot_');

    Utils.attr(leftNavTrigger, 'id', triggerID);

    const leftNavID = Utils.attr(leftNav, 'id') ? Utils.attr(leftNav, 'id') : Utils.uniqueID(5, 'apricot_');

    Utils.attr(leftNav, 'id', leftNavID);

    Utils.attr(leftNavTrigger, 'aria-controls', leftNavID);

    Utils.attr(leftNavTrigger, 'aria-expanded', 'false');

    // Skip to Content
    skip = leftNav.querySelector('.dx-left-nav-skip');
    if (skip && data.mainContentId) {
      Utils.attr(skip, 'href', `#${data.mainContentId}`);
    }

    // activate accordion
    CBAccordion({ elem: leftNav.querySelector('.cb-accordion') });

    // Page Navigation
    firstLink = Utils.getByClass('dx-left-nav-btn', leftNav)[0];

    Utils.addClass(firstLink, 'dx-first-link');

    leftNavClose = Utils.getByClass('dx-left-nav-btn', leftNav)[0];

    //Initialize breakpoint_change event
    Utils.breakpoints();

    // open/close for left nav slider
    closeShowEvents();

    //Listen to viewport changes
    trackBreakpointChanges();

    // Left Navigation
    leftNavInteraction();

    // Adjust leftNavigation items
    adjustLeftNavLayout(Utils.viewport().prefix);

    // Events
    window.addEventListener('keydown', escKeyboard, true);
  };

  if (leftNav.dxLeftNavigation !== 'cb') {
    init();
  }

  return {
    destroy: destroy,
  };
};

const CBApricotD8 = {
  menuPageLayout,
  dxLeftNavigation,
};

if (typeof window !== 'undefined') {
  window.cb = window.cb || {};

  window.cb.apricot = window.cb.apricot || {};

  window.cb.apricot.CBApricotD8 = CBApricotD8;
}

export default CBApricotD8;
